import clsx from 'clsx';
import React, {FC, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

interface DocTocItem {
  id: string;
  /** 标题层级：h2 顶格、h3 缩进（depth=3 时收录） */
  level: 2 | 3;
  text: string;
}

interface DocTocProps {
  className?: string;
  /** 收录到第几级标题：2=仅 h2（政策页默认），3=h2+h3（指南页：正文章节在 h3 上） */
  depth?: 2 | 3;
}

/**
 * 文档右侧目录（政策/指南/代理页用，恢复老 /doc/ Docusaurus 站的 TOC 交互）：
 * 挂载后扫描页面内 .v3-prose 的标题（按 depth）自动分配锚点 id，
 * IntersectionObserver 跟踪当前章节高亮，点击平滑滚动（scroll-margin-top 在 prose.css）。
 * 标题少于 2 个时整块不渲染；语言切换时重建（依赖 i18n.language）；
 * 响应式显隐（hidden xl:block）由使用方控制。
 */
const DocToc: FC<DocTocProps> = ({className, depth = 2}) => {
  const {i18n, t} = useTranslation('legal');
  const [items, setItems] = useState<DocTocItem[]>([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const selector = depth >= 3 ? '.v3-prose h2, .v3-prose h3' : '.v3-prose h2';
    const headings = Array.from(document.querySelectorAll<HTMLElement>(selector));
    const tocItems = headings
      .map((el, index) => {
        if (!el.id) el.id = `doc-sec-${index + 1}`;
        return {
          id: el.id,
          level: el.tagName === 'H3' ? (3 as const) : (2 as const),
          text: el.textContent?.trim() || '',
        };
      })
      .filter((item) => item.text);
    setItems(tocItems);
    if (tocItems[0]) setActiveId(tocItems[0].id);

    const observer = new IntersectionObserver(
      (entries) => {
        // 取视口内最靠上的标题；滚到底部时兜底激活最后一个
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        } else if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 4) {
          setActiveId(tocItems[tocItems.length - 1]?.id || '');
        }
      },
      {rootMargin: '-96px 0px -60% 0px', threshold: [0, 1]},
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [depth, i18n.language]);

  if (items.length < 2) return null;

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  return (
    <nav aria-label={t('tocTitle')} className={clsx(className)}>
      <div className="sticky top-28">
        <p className="text-sm font-semibold text-slate-900 mb-4">{t('tocTitle')}</p>
        <ul className="space-y-0.5 border-l border-slate-200">
          {items.map((item) => (
            <li key={item.id}>
              <button
                className={clsx(
                  'block w-full text-left text-sm leading-relaxed py-1.5 -ml-px border-l-2 transition-colors',
                  item.level === 3 ? 'pl-8 text-[13px]' : 'pl-4',
                  item.id === activeId
                    ? 'border-indigo-500 text-indigo-600 font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-900',
                )}
                onClick={() => scrollTo(item.id)}
                type="button"
              >
                {item.text}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default DocToc;
